<template>
  <ul
    :id="`${selectId}-listbox`"
    ref="dropdown"
    v-click-outside="closeOnClickOutside"
    role="listbox"
    :class="rootClass"
    :aria-expanded="showOptions"
    :aria-labelledby="`${selectId}-label`"
  >
    <BravadoSelectDropdownItem
      v-for="option in splittedOptions.nonRemovableOptions"
      :key="option.selectDropdownValue"
      :option="option"
      is-selected
      aria-selected
      role="option"
      tabindex="-1"
      :removable="false"
      class="bravado-select-dropdown__item"
    />
    <BravadoSelectDropdownItem
      v-for="option in splittedOptions.removableOptions"
      :key="option.selectDropdownValue"
      :option="option"
      :is-selected="isOptionSelected[option.selectDropdownValue]"
      :aria-selected="isOptionSelected[option.selectDropdownValue]"
      role="option"
      :tabindex="isElementToFocus(option) ? 0 : -1"
      class="bravado-select-dropdown__item"
      @keydown="event => handleKeyDown(event, option)"
      @toggle-option="() => handleToggleOption(option)"
    />
    <button
      v-if="isCreatable && term.length >= minLength"
      class="bravado-select-dropdown__create"
      @click="emit('create-option')"
    >
      <BravadoSelectDropdownImage :image="imageForAddingNew" class="bravado-select-dropdown__create-image" />
      <BravadoText variant="body">Add "{{ term }}"</BravadoText>
    </button>
  </ul>
</template>

<script lang="ts" setup>
import { computed, onMounted, nextTick, ref } from '@nuxtjs/composition-api';
import groupBy from 'lodash/groupBy';
import type { BravadoSelectDropdownOption, BravadoSelectDropdownOptionImage } from './types';
import { ImageShape } from './types';
import BravadoSelectDropdownItem from './BravadoSelectDropdownItem.vue';
import BravadoSelectDropdownImage from './BravadoSelectDropdownImage.vue';
import { focusOnNextOption, focusOnPreviousOption, keys } from './accessibility';
import PlusIcon from '~/assets/svg/plus.vue.svg';

type BravadoSelectDropdownProps = {
  showOptions?: boolean;
  shouldFocusOnOpen?: boolean;
  isCreatable?: boolean;
  selectedOptions?: Array<BravadoSelectDropdownOption>;
  options?: Array<BravadoSelectDropdownOption>;
  selectId: string;
  term?: string;
  minLength?: number;
  position?: 'up' | 'down';
  getIsRemovable?: (option: BravadoSelectDropdownOption) => boolean;
};
type BravadoSelectDropdownEmits = {
  (event: 'toggle-option', option: BravadoSelectDropdownOption): void;
  (event: 'close-options', payload?: MouseEvent): void;
  (event: 'create-option'): void;
};

const props = withDefaults(defineProps<BravadoSelectDropdownProps>(), {
  showOptions: false,
  shouldFocusOnOpen: false,
  isCreatable: false,
  selectedOptions: () => [],
  options: () => [],
  term: '',
  minLength: 2,
  position: 'down',
  getIsRemovable: () => false,
});
const emit = defineEmits<BravadoSelectDropdownEmits>();

const imageForAddingNew: BravadoSelectDropdownOptionImage = {
  shape: ImageShape.rounded,
  component: PlusIcon,
  props: {
    alt: 'Plus',
    width: '20px',
    height: '20px',
  },
};
const dropdown = ref<HTMLElement | null>(null);

function handleToggleOption(option: BravadoSelectDropdownOption) {
  emit('toggle-option', option);
}

const splittedOptions = computed(() =>
  groupBy(props.options, option => (props.getIsRemovable(option) ? 'removableOptions' : 'nonRemovableOptions')),
);
const optionToFirstFocus = computed(() => {
  const removableSelectedOptions = props.selectedOptions.filter(props.getIsRemovable);
  return [...removableSelectedOptions, ...splittedOptions.value.removableOptions][0];
});

function isElementToFocus(option: BravadoSelectDropdownOption) {
  return optionToFirstFocus.value.selectDropdownValue === option.selectDropdownValue;
}

const rootClass = computed(() => {
  return {
    'bravado-select-dropdown': true,
    'bravado-select-dropdown--position-up': props.position === 'up',
    'bravado-select-dropdown--position-down': props.position === 'down',
  };
});

const isOptionSelected = computed(() =>
  props.selectedOptions.reduce((acc, option) => {
    acc[option.selectDropdownValue] = true;
    return acc;
  }, {} as Record<string, boolean>),
);

const optionsElements = computed(() =>
  Array.from(document.getElementsByClassName('bravado-select-dropdown__item') as HTMLCollectionOf<HTMLElement>).filter(
    element => !element.classList.contains('bravado-select-dropdown-item--not-removable'),
  ),
);

function closeOnClickOutside(event: MouseEvent) {
  emit('close-options', event);
}

function handleKeyDown(event: KeyboardEvent, option: BravadoSelectDropdownOption) {
  const callbacks = {
    [keys.ARROW_UP.keyName]: () => focusOnPreviousOption(optionsElements.value),
    [keys.ARROW_DOWN.keyName]: () => focusOnNextOption(optionsElements.value),
    [keys.ENTER.keyName]: () => handleToggleOption(option),
    [keys.SPACE.keyName]: () => handleToggleOption(option),
    [keys.ESCAPE.keyName]: () => emit('close-options'),
  };

  callbacks[event.key]?.();
}

onMounted(() => {
  if (props.shouldFocusOnOpen) {
    const element = document.querySelector('.bravado-select-dropdown__item[tabindex="0"]') as HTMLElement;
    if (!element) return;
    nextTick(() => {
      element.focus();
    });
  }
});
</script>

<style scoped lang="scss">
.bravado-select-dropdown {
  width: 100%;
  background: var(--theme-color-card);
  border-top: none;
  box-sizing: border-box;
  max-height: 350px;
  overflow: auto;
  z-index: 101;
  top: auto;
  border-radius: 12px;
  box-shadow: var(--theme-elevation-01);
  list-style: none;
  padding: 0;
  position: absolute;
  margin: 0;

  &--position-up {
    bottom: 44px;
  }

  & > :first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  & > :last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  &__create {
    padding: 8px 16px;
    background: var(--theme-color-card);
    display: flex;
    align-items: center;
    width: 100%;
    border: none;
    color: var(--theme-color-text);

    &:focus,
    &:hover {
      background: var(--theme-color-over-card);
    }

    &:hover {
      cursor: pointer;
    }
  }

  &__create-image {
    margin-right: 8px;
  }

  &__item + *,
  &__item + &__create {
    border-top: 1px solid var(--theme-color-border);
  }
}
</style>
